'use client';

import React, { type ReactElement, type ReactNode } from 'react';

import { type PreparationProcessType } from '@/common/types/preparation-process.type';
import classNames from 'classnames';

import Tag from '@/common/components/tag/tag.component';
import Text from '@/common/components/text/text.component';
import { persianPreparationProcessConstant } from '@/common/constants/persian-preparation-process.constant';

export interface LightCardProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}

interface ContainerComponenetProps {
  children: ReactNode;
  className?: string;
}

interface OrderTagProp {
  className?: string;
  tag: PreparationProcessType;
}
export default function LightCard({
  className,
  children,
}: LightCardProps): ReactElement {
  return (
    <div
      className={classNames(
        'mt-2 flex h-full w-full flex-shrink-0 items-center justify-between overflow-hidden rounded-2xl border border-silver-200 bg-white px-2 py-2',
        className
      )}
    >
      {children}
    </div>
  );
}

function Body({ className, children }: LightCardProps) {
  return (
    <div
      className={classNames(
        'flex flex-col justify-center gap-[6px]',
        className
      )}
    >
      {children}
    </div>
  );
}

function Item({ className, children, onClick }: LightCardProps): ReactElement {
  const clickHandler = () => {
    if (onClick) {
      onClick();
    }
  };
  return (
    <div
      className={classNames(
        'flex flex-row items-center justify-center gap-[10px]',
        className
      )}
      onClick={clickHandler}
    >
      {children}
    </div>
  );
}

function Icon({ className, children }: LightCardProps) {
  return (
    <div
      className={classNames(
        'flex  h-12 w-12 items-center justify-center rounded-lg border-[1px] border-primary-100',
        className
      )}
    >
      {children}
    </div>
  );
}

function Title({ className, children }: ContainerComponenetProps) {
  return (
    <Text
      className={classNames(
        'text-xs font-medium text-black-default',
        className
      )}
    >
      {children}
    </Text>
  );
}

function OrderTime({ className, children }: ContainerComponenetProps) {
  return (
    <Text
      className={classNames(
        'text-[11px] font-normal text-black-200',
        className
      )}
    >
      {children}
    </Text>
  );
}

function Status({ tag, className }: OrderTagProp) {
  return (
    <Tag className={className} type={tag}>
      {persianPreparationProcessConstant[tag]}
    </Tag>
  );
}

LightCard.Body = Body;
LightCard.Item = Item;
LightCard.Title = Title;
LightCard.Icon = Icon;
LightCard.OrderTime = OrderTime;
LightCard.Status = Status;
